import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, Check, AlertTriangle, PieChart as PieIcon, Activity, Bot, TrendingUp, Shield, Target,
} from 'lucide-react';
import PageContainer from '../components/PageContainer';
import { api } from '../lib/api';

export default function Forecast({ incomeData, toggleSidebar, allSources }) {
  const [predictions, setPredictions] = useState(null);
  const [totalPredictions, setTotalPredictions] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState("All");
  const [monthlyGoal, setMonthlyGoal] = useState(50000);
  const [effort, setEffort] = useState(100);

  const sourceCounts = useMemo(() => {
    const counts = {};
    incomeData.forEach(item => {
      counts[item.source] = (counts[item.source] || 0) + 1;
    });
    return counts;
  }, [incomeData]);

  const MIN_DATA_POINTS = 5;

  useEffect(() => {
    if (incomeData.length > 0) {
      handlePredict("All", true);
      if (selectedSource !== "All") handlePredict(selectedSource);
    }
    // eslint-disable-next-line
  }, [incomeData]);

  useEffect(() => {
    if (incomeData.length > 0) handlePredict(selectedSource);
    // eslint-disable-next-line
  }, [selectedSource]);

  const handlePredict = async (source, isContext = false) => {
    if (!isContext) setLoading(true);
    try {
      const res = await api.post(`/predict?months=3&source=${source}`);
      if (isContext) {
        setTotalPredictions(res.data.predictions);
        if (source === "All") {
          setPredictions(res.data.predictions);
          setPatterns(res.data.patterns);
        }
      } else {
        setPredictions(res.data.predictions);
        setPatterns(res.data.patterns);
      }
    } catch (error) { console.error("Forecast error"); }
    setLoading(false);
  };

  const getAdjusted = (val) => val * (effort / 100);

  let currentProjected = 0;
  let contextProjected = 0;
  let reliabilityScore = 0;

  if (predictions) {
    currentProjected = predictions.reduce((sum, p) => sum + getAdjusted(p.predicted_income), 0);
    reliabilityScore = predictions[0].reliability || 85; // Default to 85 if undefined
  }
  if (totalPredictions) {
    contextProjected = totalPredictions.reduce((sum, p) => sum + getAdjusted(p.predicted_income), 0);
  }

  const contribution = contextProjected > 0 ? (currentProjected / contextProjected) * 100 : 0;
  // "Monthly Target" is a per-month figure, so compare it against the average
  // projected month, not the 3-month (90-day) total - otherwise the shortfall
  // gets inflated ~3x against a goal the user never actually set.
  const avgMonthlyProjected = predictions && predictions.length > 0 ? currentProjected / predictions.length : 0;
  const gap = avgMonthlyProjected - (selectedSource === "All" ? monthlyGoal : 0);
  const progress = monthlyGoal > 0 ? Math.min((avgMonthlyProjected / monthlyGoal) * 100, 100) : 0;

  const getSpecificInsights = () => {
    if (!patterns) return [];
    const list = [];

    const growthVal = patterns.growth_trend ?? null;
    if (growthVal !== null) {
      list.push({ title: "Growth Velocity", val: growthVal > 0 ? `+${growthVal}%` : `${growthVal}%`, desc: growthVal > 0 ? "Positive momentum detected vs previous period." : "Contraction detected. Increasing volume recommended.", color: growthVal > 0 ? "green" : "red" });
    } else {
      list.push({ title: "Growth Velocity", val: "N/A", desc: "Not enough data to calculate trend.", color: "blue" });
    }

    list.push({
      title: "Income Stability",
      val: reliabilityScore > 75 ? "High" : "Moderate",
      desc: reliabilityScore > 75 ? "Revenue stream is predictable." : "Expect significant weekly variance.",
      color: reliabilityScore > 75 ? "blue" : "orange"
    });

    list.push({
      title: "Optimization",
      val: patterns.best_day,
      desc: `Historical data suggests ${patterns.best_day}s yield highest returns.`,
      color: "purple"
    });

    return list;
  };

  return (
    <PageContainer title="Strategic Forecast" subtitle="AI-Driven Revenue Modeling" toggleSidebar={toggleSidebar}>

      <div className="luxury-controls">
        <div className="lux-group">
          <label>Forecast Scope</label>
          <select value={selectedSource} onChange={e => setSelectedSource(e.target.value)}>
            <option value="All">Total Portfolio ({incomeData.length} entries)</option>
            {allSources.map(s => {
              const count = sourceCounts[s] || 0;
              const isDisabled = count < MIN_DATA_POINTS;
              return (
                <option key={s} value={s} disabled={isDisabled}>
                  {s} {isDisabled ? `(Insufficient Data: ${count})` : `(${count} entries)`}
                </option>
              )
            })}
          </select>
        </div>
        <div className="lux-group">
          <label>Monthly Target</label>
          <div className="lux-input">
            <span>₹</span>
            <input type="number" value={monthlyGoal} onChange={e => setMonthlyGoal(e.target.value)} />
          </div>
        </div>
        <div className="lux-slider-group">
          <div className="slider-top"><label>Planned Workload</label><span className="effort-badge">{effort}%</span></div>
          <input type="range" min="50" max="150" value={effort} onChange={(e) => setEffort(e.target.value)} className="lux-slider" />
        </div>
        <button className="btn-lux-glow" onClick={() => handlePredict(selectedSource)} disabled={loading}>
          {loading ? <div className="spinner-small" /> : <Sparkles size={18} />}
          <span>Update Model</span>
        </button>
      </div>

      {predictions ? (
        <div className="pro-grid-optimized">

          <div className="quarterly-pro">
            <div className="q-left">
              <span className="q-lbl">
                {selectedSource === 'All' ? 'Avg. Monthly Projection' : `${selectedSource} Contribution`}
              </span>
              <div className="q-big">₹{(selectedSource === 'All' ? avgMonthlyProjected : currentProjected).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>

              {selectedSource === "All" ? (
                <div className="q-gap-badge">
                  {gap >= 0
                    ? <span className="badge-success"><Check size={14} /> +{Math.abs(gap).toLocaleString(undefined, { maximumFractionDigits: 0 })} Surplus</span>
                    : <span className="badge-warning"><AlertTriangle size={14} /> -{Math.abs(gap).toLocaleString(undefined, { maximumFractionDigits: 0 })} Shortfall</span>
                  }
                </div>
              ) : (
                <div className="q-gap-badge">
                  <span className="badge-info"><PieIcon size={14} /> {contribution.toFixed(1)}% of Total Income</span>
                </div>
              )}
            </div>

            <div className="q-divider"></div>

            <div className="q-right-interactive">
              <div className="sim-top"><label>Model Confidence</label></div>
              <div className={`signal-badge ${reliabilityScore > 80 ? 'sig-high' : reliabilityScore > 50 ? 'sig-med' : 'sig-low'}`}>
                <Activity size={16} />
                <span>{reliabilityScore > 80 ? 'High Precision' : reliabilityScore > 50 ? 'Standard Variance' : 'High Volatility'} ({reliabilityScore}%)</span>
              </div>

              {selectedSource === "All" && (
                <>
                  <div className="mini-goal-track">
                    <motion.div className="mini-goal-fill" initial={{ width: 0 }} animate={{ width: `${progress}%` }} style={{ background: gap >= 0 ? '#10B981' : '#3B82F6' }} />
                  </div>
                  <div className="goal-status-text">{progress.toFixed(0)}% of Target Reached</div>
                </>
              )}
            </div>
          </div>

          <div className="pro-months-row">
            {predictions.map((pred, i) => {
              const adjusted = getAdjusted(pred.predicted_income);
              const growth = i > 0 ? ((adjusted - getAdjusted(predictions[i - 1].predicted_income)) / getAdjusted(predictions[i - 1].predicted_income)) * 100 : 0;
              return (
                <motion.div key={i} className="pro-month-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                  <div className="month-header-row">
                    <span className="month-name">{pred.month}</span>
                    {i === 0 && <span className="tag-current">Next</span>}
                  </div>
                  <h1 className="pm-val">₹{adjusted.toLocaleString(undefined, { maximumFractionDigits: 0 })}</h1>
                  {i > 0 && (
                    <div className={`pm-trend ${growth >= 0 ? 't-green' : 't-orange'}`}>
                      {growth >= 0 ? '+' : ''}{growth.toFixed(1)}% vs prev
                    </div>
                  )}
                </motion.div>
              )
            })}
          </div>

          <div className="insight-card-pro full-width">
            <div className="insight-header">
              <h3><Bot size={20} color="#3B82F6" /> AI Strategic Brief</h3>
            </div>
            <div className="insight-grid-layout">

              <div className="insight-main-text">
                <p>
                  {selectedSource === "All"
                    ? (gap >= 0
                      ? "You are positioned to exceed your monthly target based on current momentum. Recommend maintaining current volume while optimizing for efficiency."
                      : `Projected monthly volume is below target by ₹${Math.abs(gap).toLocaleString(undefined, { maximumFractionDigits: 0 })}. To bridge this gap, consider increasing your workload by approximately ${Math.ceil((Math.abs(gap) / avgMonthlyProjected) * 100)}% or diversifying income sources.`)
                    : `Analysis isolated for ${selectedSource}. This stream currently accounts for ${contribution.toFixed(1)}% of your total projected wealth. ${patterns.growth_trend > 0 ? "Momentum is positive." : "Growth is currently stagnant."}`
                  }
                </p>
              </div>

              <div className="insight-metrics-row">
                {getSpecificInsights().map((item, i) => (
                  <div key={i} className="metric-tile">
                    <div className={`tile-icon ${item.color}`}>{item.title === "Growth Velocity" ? <TrendingUp size={16} /> : item.title === "Income Stability" ? <Shield size={16} /> : <Target size={16} />}</div>
                    <div className="tile-content">
                      <span className="tile-lbl">{item.title}</span>
                      <span className="tile-val">{item.val}</span>
                      <span className="tile-desc">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      ) : (
        <div className="empty-state-large">
          <Sparkles size={48} style={{ color: '#3B82F6', marginBottom: 20 }} />
          <h3>Initialize Model</h3>
          <p>Add transactions to generate your forecast.</p>
        </div>
      )}
    </PageContainer>
  );
}
